import { isTourTargetReady } from './targetReadiness';
import { findVisibleTourTarget, selectorForTourTarget } from './tourTargets';
import type { TourTargetAction } from './types';

export const TARGET_WAIT_TIMEOUT_MS = 15000;
const READY_TRANSITION_GRACE_MS = 100;

const scheduleTimeout = (callback: () => void, delayMs: number): number =>
  typeof window === 'undefined'
    ? (globalThis.setTimeout(callback, delayMs) as unknown as number)
    : window.setTimeout(callback, delayMs);

const requestNextFrame = (callback: () => void): number => {
  if (typeof window === 'undefined') return scheduleTimeout(callback, 16);
  if (typeof window.requestAnimationFrame !== 'function') {
    return scheduleTimeout(callback, 16);
  }

  return window.requestAnimationFrame(() => scheduleTimeout(callback, 0));
};

const waitForCondition = async (condition: () => boolean, timeoutMs: number): Promise<boolean> => {
  const startedAt = Date.now();

  return new Promise((resolve) => {
    const check = (): void => {
      if (typeof document === 'undefined') {
        resolve(false);
        return;
      }

      if (condition()) {
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(false);
        return;
      }

      requestNextFrame(check);
    };

    check();
  });
};

/**
 * Calling .click() on a disabled form control is a no-op per spec - no event fires, nothing
 * happens - but that gives no signal on its own. Without this check the caller reports success
 * and a subsequent wait-until-ready action polls the full timeout before giving up, turning a
 * blocked step (e.g. Save disabled by a validation warning) into a silent multi-second hang
 * instead of failing immediately.
 */
const isDisabledFormControl = (element: HTMLElement): boolean =>
  (element instanceof HTMLButtonElement ||
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement) &&
  element.disabled;

export const clickTourTarget = (target: string): boolean => {
  const element = findVisibleTourTarget(target);
  if (!(element instanceof HTMLElement)) return false;
  if (isDisabledFormControl(element)) return false;

  element.click();
  return true;
};

export const submitTourTarget = (target: string): boolean => {
  const element = findVisibleTourTarget(target);
  if (!(element instanceof HTMLFormElement)) return false;

  const submitState = { didDispatch: false };
  const handleSubmit = (): void => {
    submitState.didDispatch = true;
  };
  element.addEventListener('submit', handleSubmit, { once: true });

  if (typeof element.requestSubmit === 'function') {
    try {
      element.requestSubmit();
    } catch {
      // Fall back to a bubbling submit event below.
    }
  }

  element.removeEventListener('submit', handleSubmit);
  if (!submitState.didDispatch) {
    element.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true }));
  }

  return true;
};

export const performTourTargetAction = (stepTarget: string, action: TourTargetAction): boolean => {
  const target = action.target ?? stepTarget;

  switch (action.type) {
    case 'click':
      return clickTourTarget(target);
    case 'submit':
      return submitTourTarget(target);
  }
};

export const waitForTourTarget = (
  target: string,
  timeoutMs = TARGET_WAIT_TIMEOUT_MS,
): Promise<boolean> =>
  waitForCondition(
    () => typeof document !== 'undefined' && findVisibleTourTarget(target) !== null,
    timeoutMs,
  );

export const waitForTourTargetReady = (
  target: string,
  timeoutMs = TARGET_WAIT_TIMEOUT_MS,
): Promise<boolean> =>
  waitForCondition(
    () => !isTourTargetReady(target),
    Math.min(timeoutMs, READY_TRANSITION_GRACE_MS),
  ).then((didBecomeNotReady) => {
    if (!didBecomeNotReady) return isTourTargetReady(target);
    return waitForCondition(() => isTourTargetReady(target), timeoutMs);
  });

export const waitForTourTargetRemoval = (
  target: string,
  timeoutMs = TARGET_WAIT_TIMEOUT_MS,
): Promise<boolean> =>
  waitForCondition(
    () =>
      typeof document === 'undefined' ||
      document.querySelector(selectorForTourTarget(target)) === null,
    timeoutMs,
  );
